import type {
  EncryptedDocIndexEntry,
  EncryptedFolderNode,
  ItemPlacementNode,
  PageParentKind,
  PageSearchRequest,
  PageSearchResponse,
  TeamClientMessage,
  TeamServerMessage,
  TypePlacementNode,
} from '@nimbalyst/collab-protocol';
import { applyPageFieldsPatch, normalizePageFields, PageSearchRequests, samePageFields } from '@nimbalyst/collab-protocol';
import {
  appendSyncClientParams,
  type CollabDocsCommand,
  type CollabDocsCommandResult,
  type CollabDocsDataSource,
  type SharedDocument,
  type SharedFolder,
} from '@nimbalyst/collab-bundle/docs-ui';
import {
  TeamPageLinksRequests,
  TeamPageMarksRequests,
  type TeamPageLinksFilters,
  type TeamPageLinksResult,
  type TeamPageMarksFilters,
  type TeamPageMarksResult,
} from '@nimbalyst/collab-bundle/pages';
import { WebWriteConfirmations } from './webWriteConfirmations';

type DataChange = Parameters<Parameters<CollabDocsDataSource['subscribe']>[0]>[0];
type TypePlacement = NonNullable<CollabDocsCommandResult['typePlacements']>[number];
type ItemPlacement = NonNullable<CollabDocsCommandResult['itemPlacements']>[number];

export interface WebCollabDocumentsDataSourceOptions {
  orgId: string;
  teamProjectId: string | null;
  serverUrl: string;
  getTeamJwt: () => Promise<string>;
  createWebSocket?: (url: string) => WebSocket;
  reconnectDelaysMs?: readonly number[];
  /** How long a confirmed write waits for the server; defaults to 6s. */
  confirmTimeoutMs?: number;
}

export interface WebDocumentCommentNotification {
  documentId: string;
  reason: 'mention' | 'reply';
  recipientUserIds: string[];
  payload: {
    actorName?: string;
    sourceTitle?: string;
    snippet?: string;
    commentId?: string;
    threadId?: string;
  };
}

const DEFAULT_RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 5_000, 10_000, 30_000] as const;

/**
 * `known` is the row already held: an entry that does not carry fields (an
 * older server, or fields the server could not read) keeps its fields; null
 * means the page has none.
 */
function mapDocument(entry: EncryptedDocIndexEntry, known?: SharedDocument): SharedDocument {
  const decryptFailed = entry.titleIv.length > 0;
  const fields = entry.fields === undefined ? known?.fields : normalizePageFields(entry.fields);
  return {
    documentId: entry.documentId,
    teamProjectId: entry.projectId ?? null,
    title: decryptFailed ? '' : entry.encryptedTitle,
    documentType: entry.documentType,
    metadataVersion: entry.metadataVersion,
    fileExtension: entry.fileExtension,
    editorId: entry.editorId,
    createdBy: entry.createdBy,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    lastWriterUserId: entry.lastWriterUserId ?? null,
    parentFolderId: entry.parentFolderId ?? null,
    // Absent from older servers: a page parent with no order, as on the desktop.
    parentKind: entry.parentKind ?? 'page',
    sortOrder: entry.sortOrder ?? null,
    trashedAt: entry.trashedAt ?? null,
    // Absent from older servers: treated as having content, so no folder icon.
    hasContent: entry.hasContent,
    ...(fields && Object.keys(fields).length > 0 ? { fields } : {}),
    decryptFailed: decryptFailed || undefined,
  };
}

function mapFolder(entry: EncryptedFolderNode): SharedFolder {
  const decryptFailed = entry.nameIv.length > 0;
  return {
    folderId: entry.folderId,
    parentFolderId: entry.parentFolderId ?? null,
    name: decryptFailed ? '' : entry.encryptedName,
    sortOrder: entry.sortOrder,
    createdBy: entry.createdBy,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    decryptFailed: decryptFailed || undefined,
  };
}

function mapTypePlacement(entry: TypePlacementNode): TypePlacement {
  return {
    typeId: entry.typeId,
    projectId: entry.projectId,
    parentFolderId: entry.parentFolderId,
    parentKind: entry.parentKind,
    sortOrder: entry.sortOrder,
    createdBy: entry.createdBy,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
  };
}

function mapItemPlacement(entry: ItemPlacementNode): ItemPlacement {
  return {
    itemId: entry.itemId,
    projectId: entry.projectId,
    parentId: entry.parentId,
    parentKind: entry.parentKind,
    sortOrder: entry.sortOrder,
    createdBy: entry.createdBy,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
  };
}

/** Parent kind and order for the wire; absent options stay off it for older servers. */
function placementFields(command: { parentKind?: PageParentKind; sortOrder?: number | null }) {
  return {
    ...(command.parentKind ? { parentKind: command.parentKind } : {}),
    ...(command.sortOrder !== undefined ? { sortOrder: command.sortOrder } : {}),
  };
}

function mutationKey(message: TeamClientMessage): string | null {
  switch (message.type) {
    case 'docIndexRegister':
    case 'docIndexUpdate':
    case 'docIndexSetFields':
    case 'docIndexRemove':
    case 'docTrash':
    case 'docRestore':
    case 'docMove':
      return `${message.type}:${message.documentId}`;
    case 'folderRegister':
    case 'folderRename':
    case 'folderMove':
    case 'folderRemove':
      return `${message.type}:${message.folderId}`;
    case 'typePlacementSet':
    case 'typePlacementRemove':
      // One key per type: only the last intent survives a disconnect.
      return `typePlacement:${message.typeId}`;
    case 'itemPlacementSet':
    case 'itemPlacementRemove':
      return `itemPlacement:${message.itemId}`;
    default:
      return null;
  }
}

/** Browser TeamRoom projection. Tree, discovery, and recency derivation stay in collab-client. */
export class WebCollabDocumentsDataSource implements CollabDocsDataSource {
  private readonly listeners = new Set<(change: DataChange) => void>();
  private readonly documents = new Map<string, SharedDocument>();
  private readonly folders = new Map<string, SharedFolder>();
  /** This project's type placements; null until the server sends a list. */
  private typePlacements: Map<string, TypePlacement> | null = null;
  /** This project's item placements; null until the server sends a list. */
  private itemPlacements: Map<string, ItemPlacement> | null = null;
  /** Set once the TeamRoom converted folders into pages. */
  private pageTree = false;
  /** Set once the TeamRoom said it stores plain-page fields. */
  private pageFields = false;
  /** Set when the server echoes the author's page moves and names their request id in a refusal. */
  private authorWriteEcho = false;
  private readonly confirmations: WebWriteConfirmations;
  private primaryProjectId: string | null = null;
  /** Set by the first teamSync reply, which names the primary project. */
  private primaryProjectKnown = false;
  private readonly pendingMutations = new Map<string, TeamClientMessage>();
  private readonly pageMarkRequests = new TeamPageMarksRequests();
  private readonly pageLinkRequests = new TeamPageLinksRequests();
  private readonly pageSearchRequests = new PageSearchRequests();
  private readonly snapshotWaiters = new Set<{
    resolve: (snapshot: Awaited<ReturnType<CollabDocsDataSource['snapshot']>>) => void;
    reject: (error: Error) => void;
  }>();
  private readonly folderRefreshWaiters = new Set<(folders: SharedFolder[] | null) => void>();
  private readonly placementRefreshWaiters = new Set<(placements: TypePlacement[] | null) => void>();
  private readonly itemPlacementRefreshWaiters = new Set<(placements: ItemPlacement[] | null) => void>();
  private socket: WebSocket | null = null;
  private connectionPromise: Promise<void> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private currentStatus: ReturnType<CollabDocsDataSource['status']> = 'disconnected';
  private documentsReady = false;
  private foldersReady = false;
  private resyncInFlight = false;
  private disposed = false;

  constructor(private readonly options: WebCollabDocumentsDataSourceOptions) {
    this.confirmations = new WebWriteConfirmations(options.confirmTimeoutMs);
  }

  async snapshot() {
    await this.ensureConnected();
    if (this.documentsReady && this.foldersReady) return this.currentSnapshot();
    return new Promise<Awaited<ReturnType<CollabDocsDataSource['snapshot']>>>((resolve, reject) => {
      this.snapshotWaiters.add({ resolve, reject });
    });
  }

  subscribe(cb: (change: DataChange) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  status() {
    return this.currentStatus;
  }

  /**
   * The index row for one document, or null while the index is still syncing.
   *
   * Read synchronously so the document route can resolve which editor owns a
   * document without a second round trip. The atoms hold the same rows, but
   * they belong to the bundle's Jotai instance — a host reading them through
   * its own import gets a second store and an empty answer.
   */
  getDocument(documentId: string): SharedDocument | null {
    return this.documents.get(documentId) ?? null;
  }

  /**
   * Whether a confirmed page move can be confirmed. An older server sends the
   * author no echo, so such a move resolves once sent and proves nothing.
   */
  echoesAuthorWrites(): boolean {
    return this.authorWriteEcho;
  }

  /** Marks from the team's marks index; null while disconnected or unanswered. Never queued. */
  queryPageMarks(filters: TeamPageMarksFilters, timeoutMs = 8_000): Promise<TeamPageMarksResult | null> {
    return this.pageMarkRequests.request((message) => this.sendQuery(message), filters, timeoutMs);
  }

  /** Body links from the team's page links index; null while disconnected or unanswered. Never queued. */
  queryPageLinks(filters: TeamPageLinksFilters, timeoutMs = 8_000): Promise<TeamPageLinksResult | null> {
    return this.pageLinkRequests.request((message) => this.sendQuery(message), filters, timeoutMs);
  }

  /** This project's pages whose bodies match, from the team's search index; null while disconnected or unanswered. */
  searchPages(request: PageSearchRequest, timeoutMs = 8_000): Promise<PageSearchResponse | null> {
    const projectId = this.options.teamProjectId ?? this.primaryProjectId;
    if (!projectId) return Promise.resolve(null);
    return this.pageSearchRequests.request((message) => this.sendQuery(message), projectId, request, timeoutMs);
  }

  /** A read goes out only on an open socket; false tells the caller to answer null. */
  private sendQuery(message: TeamClientMessage): boolean {
    if (this.socket?.readyState !== 1) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  /** Announce a document comment over the TeamRoom connection owned here. */
  notifyDocumentComment(input: WebDocumentCommentNotification): void {
    if (input.recipientUserIds.length === 0) return;
    const commentId = input.payload.commentId;
    if (!commentId) {
      // The server uses commentId as its inbox idempotency key; sending without
      // one would let a retry duplicate a recipient's row, so drop it loudly.
      console.warn(
        `[WebCollabDocumentsDataSource] Skipping ${input.reason} notification without a comment id`,
      );
      return;
    }

    const preview = {
      ...(input.payload.actorName ? { actorLabel: input.payload.actorName } : {}),
      ...(input.payload.sourceTitle ? { sourceTitle: input.payload.sourceTitle } : {}),
      ...(input.payload.snippet ? { snippet: input.payload.snippet } : {}),
    };
    this.send({
      type: 'documentCommentNotify',
      documentId: input.documentId,
      commentId,
      ...(input.payload.threadId ? { threadId: input.payload.threadId } : {}),
      reason: input.reason,
      recipientUserIds: input.recipientUserIds,
      ...(Object.keys(preview).length > 0 ? { preview } : {}),
    });
  }

  async command(command: CollabDocsCommand): Promise<CollabDocsCommandResult> {
    if (command.type === 'reconnect') {
      this.reconnectNow();
      return { ok: true };
    }
    await this.ensureConnected();
    switch (command.type) {
      case 'register-document':
        this.send({
          type: 'docIndexRegister',
          documentId: command.documentId,
          encryptedTitle: command.title,
          titleIv: '',
          documentType: command.documentType,
          ...command.metadata,
          projectId: this.options.teamProjectId,
          parentFolderId: command.parentFolderId,
          ...placementFields(command),
        });
        return { ok: true };
      case 'update-document-title':
        this.send({
          type: 'docIndexUpdate',
          documentId: command.documentId,
          encryptedTitle: command.title,
          titleIv: '',
        });
        return { ok: true };
      case 'set-document-fields': {
        if (!this.pageFields) throw new Error('This server cannot store page fields yet. Try again after the server is updated.');
        const requestId = crypto.randomUUID();
        // The echo holds the patch when applying it again changes nothing.
        const confirmed = this.confirmations.expect<SharedDocument>('doc', command.documentId, (row) =>
          !!row && samePageFields(applyPageFieldsPatch(row.fields, command.fields), row.fields), { requestId, sent: this.isOpen() });
        this.send({ type: 'docIndexSetFields', documentId: command.documentId, fields: command.fields, requestId });
        await confirmed;
        return { ok: true };
      }
      case 'remove-document':
        // Only Trash's permanent delete purges; a plain remove never deletes a trashed page.
        this.send({ type: 'docIndexRemove', documentId: command.documentId, ...(command.purge ? { purge: true } : {}) });
        return { ok: true };
      case 'trash-document':
        this.send({ type: 'docTrash', documentId: command.documentId, trashedAt: command.trashedAt });
        return { ok: true };
      case 'restore-document':
        this.send({ type: 'docRestore', documentId: command.documentId });
        return { ok: true };
      case 'move-document': {
        const parentKind = command.parentFolderId ? command.parentKind ?? 'page' : 'page';
        const requestId = command.confirm && this.authorWriteEcho ? crypto.randomUUID() : null;
        const confirmed = requestId
          ? this.confirmations.expect<SharedDocument>('doc', command.documentId, (row) =>
            !!row && row.parentFolderId === command.parentFolderId && row.parentKind === parentKind, { requestId, sent: this.isOpen() })
          : null;
        this.send({
          type: 'docMove',
          documentId: command.documentId,
          newParentFolderId: command.parentFolderId,
          ...placementFields(command),
          ...(requestId ? { requestId } : {}),
        });
        await confirmed;
        return { ok: true };
      }
      case 'register-folder':
        this.send({
          type: 'folderRegister',
          folderId: command.folderId,
          encryptedName: command.name,
          nameIv: '',
          parentFolderId: command.parentFolderId,
          sortOrder: command.sortOrder,
          projectId: this.options.teamProjectId,
        });
        return { ok: true };
      case 'rename-folder':
        this.send({
          type: 'folderRename',
          folderId: command.folderId,
          encryptedName: command.name,
          nameIv: '',
        });
        return { ok: true };
      case 'move-folder':
        this.send({ type: 'folderMove', folderId: command.folderId, newParentFolderId: command.parentFolderId });
        return { ok: true };
      case 'remove-folder':
        this.send({ type: 'folderRemove', folderId: command.folderId });
        return { ok: true };
      case 'refresh-folders':
        return { ok: true, folders: await this.refreshFolders() };
      case 'set-type-placement': {
        const parentKind = command.parentFolderId ? command.parentKind ?? 'page' : 'page';
        const confirmed = command.confirm
          ? this.confirmations.expect<TypePlacement>('type', command.typeId, (row) =>
            !!row && row.parentFolderId === command.parentFolderId && (row.parentKind ?? 'page') === parentKind, { sent: this.isOpen() })
          : null;
        this.send({
          type: 'typePlacementSet',
          typeId: command.typeId,
          parentFolderId: command.parentFolderId,
          sortOrder: command.sortOrder,
          ...(command.parentKind ? { parentKind: command.parentKind } : {}),
          projectId: this.options.teamProjectId,
        });
        await confirmed;
        return { ok: true };
      }
      case 'remove-type-placement':
        this.send({ type: 'typePlacementRemove', typeId: command.typeId, projectId: this.options.teamProjectId });
        return { ok: true };
      case 'refresh-type-placements':
        return { ok: true, typePlacements: await this.refreshTypePlacements() };
      // Resolve only once the server echoed the placement; reject on a refusal or timeout.
      case 'set-item-placement': {
        const confirmed = this.confirmations.expect<ItemPlacement>('item', command.itemId, (row) =>
          !!row && row.parentId === command.parentId, { sent: this.isOpen() });
        this.send({
          type: 'itemPlacementSet',
          itemId: command.itemId,
          parentId: command.parentId,
          sortOrder: command.sortOrder,
          ...(command.parentKind ? { parentKind: command.parentKind } : {}),
          projectId: this.options.teamProjectId,
        });
        await confirmed;
        return { ok: true };
      }
      case 'remove-item-placement': {
        const confirmed = this.confirmations.expect('item', command.itemId, (row) => row === null, { sent: this.isOpen() });
        this.send({ type: 'itemPlacementRemove', itemId: command.itemId, projectId: this.options.teamProjectId });
        await confirmed;
        return { ok: true };
      }
      case 'refresh-item-placements':
        return { ok: true, itemPlacements: await this.refreshItemPlacements() };
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.listeners.clear();
    this.pendingMutations.clear();
    this.confirmations.dispose();
    this.pageMarkRequests.cancelAll();
    this.pageLinkRequests.cancelAll();
    this.pageSearchRequests.cancelAll();
    const error = new Error('Team document index was disposed');
    for (const waiter of this.snapshotWaiters) waiter.reject(error);
    this.snapshotWaiters.clear();
    for (const waiter of this.folderRefreshWaiters) waiter(null);
    this.folderRefreshWaiters.clear();
    for (const waiter of this.placementRefreshWaiters) waiter(null);
    this.placementRefreshWaiters.clear();
    for (const waiter of this.itemPlacementRefreshWaiters) waiter(null);
    this.itemPlacementRefreshWaiters.clear();
    this.setStatus('disconnected');
  }

  private currentSnapshot() {
    return {
      items: [...this.documents.values()],
      containers: [...this.folders.values()],
      // Omitted until the server sends a list, so an older server never reads as empty.
      ...(this.typePlacements ? { typePlacements: [...this.typePlacements.values()] } : {}),
      ...(this.itemPlacements ? { itemPlacements: [...this.itemPlacements.values()] } : {}),
      ...(this.pageTree ? { pageTree: true } : {}),
      ...(this.pageFields ? { pageFields: true } : {}),
      // The session keeps one project in the window and other projects' rows
      // for links only; a null-project row belongs to this primary.
      ...(this.primaryProjectKnown ? { primaryProjectId: this.primaryProjectId } : {}),
    };
  }

  /** A TeamRoom holds every project's placements; this source serves one. */
  private isOwnProject(projectId: string | null): boolean {
    const own = this.options.teamProjectId ?? this.primaryProjectId;
    return own === null || own === projectId;
  }

  /** Placement changes travel as snapshot changes; see CollabDocsSnapshot. */
  private emitPlacementSnapshot(): void {
    if (this.documentsReady && this.foldersReady) this.emit({ type: 'snapshot', snapshot: this.currentSnapshot() });
  }

  private emit(change: DataChange): void {
    for (const listener of this.listeners) listener(change);
  }

  private setStatus(status: ReturnType<CollabDocsDataSource['status']>): void {
    if (this.currentStatus === status) return;
    this.currentStatus = status;
    this.emit({ type: 'status', status });
  }

  private ensureConnected(): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('Team document index was disposed'));
    if (this.socket) return Promise.resolve();
    this.connectionPromise ??= this.connect().finally(() => {
      this.connectionPromise = null;
    });
    return this.connectionPromise;
  }

  private async connect(): Promise<void> {
    this.setStatus('connecting');
    const jwt = await this.options.getTeamJwt();
    if (this.disposed) return;
    const roomId = `org:${this.options.orgId}:team`;
    const separator = this.options.serverUrl.endsWith('/') ? '' : '/';
    const url = appendSyncClientParams(
      `${this.options.serverUrl}${separator}sync/${roomId}?token=${encodeURIComponent(jwt)}`,
    );
    const socket = this.options.createWebSocket?.(url) ?? new WebSocket(url);
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.reconnectAttempt = 0;
      this.documentsReady = false;
      this.foldersReady = false;
      this.setStatus('syncing');
      socket.send(JSON.stringify({ type: 'teamSync' } satisfies TeamClientMessage));
      // Marks lists and Links sections that went unanswered while offline ask again.
      this.pageMarkRequests.changed();
      this.pageLinkRequests.changed();
    });
    socket.addEventListener('message', (event) => {
      if (this.socket !== socket) return;
      this.handleMessage(String(event.data));
    });
    socket.addEventListener('close', () => {
      if (this.socket !== socket) return;
      this.socket = null;
      // A reply can no longer arrive on this socket; reads answer "unavailable" now, not at their timeout.
      this.pageMarkRequests.cancelAll();
      this.pageLinkRequests.cancelAll();
      this.pageSearchRequests.cancelAll();
      this.setStatus('disconnected');
      this.scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      if (this.socket !== socket) return;
      socket.close();
    });
  }

  private handleMessage(raw: string): void {
    let message: TeamServerMessage;
    try {
      message = JSON.parse(raw) as TeamServerMessage;
    } catch (error) {
      console.error('[WebCollabDocumentsDataSource] Invalid TeamRoom message', error);
      return;
    }
    switch (message.type) {
      case 'teamSyncResponse':
        this.primaryProjectId = message.team.metadata?.teamProjectId ?? null;
        this.primaryProjectKnown = true;
        this.pageTree = message.team.pageTree === true;
        this.pageFields = message.team.pageFields === true;
        this.authorWriteEcho = message.team.authorWriteEcho === true;
        if (message.team.typePlacements) this.applyTypePlacements(message.team.typePlacements);
        if (message.team.itemPlacements) this.applyItemPlacements(message.team.itemPlacements);
        this.send({ type: 'docIndexSync' });
        this.send({ type: 'folderIndexSync' });
        return;
      case 'typePlacementIndexSyncResponse':
        this.applyTypePlacements(message.placements);
        return;
      case 'typePlacementBroadcast':
        if (!this.isOwnProject(message.placement.projectId)) return;
        this.typePlacements ??= new Map();
        this.typePlacements.set(message.placement.typeId, mapTypePlacement(message.placement));
        this.confirmations.changed('type', message.placement.typeId, this.typePlacements.get(message.placement.typeId));
        this.emitPlacementSnapshot();
        return;
      case 'typePlacementRemoveBroadcast':
        if (!this.isOwnProject(message.projectId) || !this.typePlacements) return;
        for (const typeId of message.typeIds) {
          this.typePlacements.delete(typeId);
          this.confirmations.changed('type', typeId, null);
        }
        this.emitPlacementSnapshot();
        return;
      case 'itemPlacementIndexSyncResponse':
        this.applyItemPlacements(message.placements);
        return;
      case 'itemPlacementBroadcast':
        if (!this.isOwnProject(message.placement.projectId)) return;
        this.itemPlacements ??= new Map();
        this.itemPlacements.set(message.placement.itemId, mapItemPlacement(message.placement));
        this.confirmations.changed('item', message.placement.itemId, this.itemPlacements.get(message.placement.itemId));
        this.emitPlacementSnapshot();
        return;
      case 'itemPlacementRemoveBroadcast':
        if (!this.isOwnProject(message.projectId) || !this.itemPlacements) return;
        for (const itemId of message.itemIds) {
          this.itemPlacements.delete(itemId);
          this.confirmations.changed('item', itemId, null);
        }
        this.emitPlacementSnapshot();
        return;
      case 'docIndexSyncResponse':
        const known = new Map(this.documents);
        this.documents.clear();
        for (const entry of message.documents) this.documents.set(entry.documentId, mapDocument(entry, known.get(entry.documentId)));
        this.confirmations.loaded('doc', this.documents);
        this.documentsReady = true;
        this.emitSnapshotIfReady();
        return;
      case 'folderIndexSyncResponse': {
        this.folders.clear();
        const folders = message.folders.map(mapFolder);
        for (const folder of folders) this.folders.set(folder.folderId, folder);
        this.foldersReady = true;
        for (const waiter of this.folderRefreshWaiters) waiter(folders);
        this.folderRefreshWaiters.clear();
        this.emitSnapshotIfReady();
        return;
      }
      case 'docIndexBroadcast': {
        const document = mapDocument(message.document, this.documents.get(message.document.documentId));
        this.documents.set(document.documentId, document);
        this.confirmations.changed('doc', document.documentId, document);
        this.emit({ type: 'items-upserted', items: [document] });
        return;
      }
      case 'docIndexRemoveBroadcast':
        this.documents.delete(message.documentId);
        this.emit({ type: 'items-removed', itemIds: [message.documentId] });
        return;
      case 'folderBroadcast': {
        const folder = mapFolder(message.folder);
        this.folders.set(folder.folderId, folder);
        this.emit({ type: 'containers-upserted', containers: [folder] });
        return;
      }
      case 'folderRemoveBroadcast':
        for (const folderId of message.folderIds) this.folders.delete(folderId);
        for (const documentId of message.documentIds) this.documents.delete(documentId);
        this.emit({
          type: 'containers-removed',
          containerIds: message.folderIds,
          itemIds: message.documentIds,
        });
        return;
      case 'pageMarksResponse':
        this.pageMarkRequests.receive(message);
        return;
      case 'pageMarksChanged':
        this.pageMarkRequests.changed();
        return;
      case 'pageLinksResponse':
        this.pageLinkRequests.receive(message);
        return;
      case 'pageLinksChanged':
        this.pageLinkRequests.changed();
        return;
      case 'pageSearchResponse':
        this.pageSearchRequests.receive(message);
        return;
      case 'documentCommentNotifyAck':
        console.info(
          `[WebCollabDocumentsDataSource] Document comment notification acknowledged: delivered ${message.deliveredUserIds.length}, suppressed ${message.suppressedUserIds.length}`,
        );
        return;
      case 'error':
        if (message.code === 'document_comment_forbidden') {
          // The editor capability gate should make this unreachable. Treat a
          // rejection as a violated client/server authorization invariant.
          console.error(
            '[WebCollabDocumentsDataSource] Invariant violation: TeamRoom rejected a document comment notification',
            message.message,
          );
          return;
        }
        console.error(`[WebCollabDocumentsDataSource] TeamRoom rejected the request (${message.code}): ${message.message}`);
        this.confirmations.error(message.requestId, message.message);
        // Index mutations are fire-and-forget, so the caller has already
        // applied optimistic state that the server just refused. Without a
        // resync a read-only user keeps seeing a phantom create/rename/trash
        // until they reconnect. Pull the authoritative index instead.
        this.resyncIndex();
        return;
      default:
        return;
    }
  }

  /** Re-pull the authoritative index after the server refused a mutation. */
  private resyncIndex(): void {
    if (this.disposed || this.resyncInFlight) return;
    this.resyncInFlight = true;
    this.send({ type: 'docIndexSync' });
    this.send({ type: 'folderIndexSync' });
    if (this.typePlacements) this.send({ type: 'typePlacementIndexSync' });
    if (this.itemPlacements) this.send({ type: 'itemPlacementIndexSync' });
  }

  private applyTypePlacements(placements: TypePlacementNode[]): void {
    this.typePlacements = new Map(placements
      .filter((placement) => this.isOwnProject(placement.projectId))
      .map((placement) => [placement.typeId, mapTypePlacement(placement)]));
    this.confirmations.loaded('type', this.typePlacements);
    const list = [...this.typePlacements.values()];
    for (const waiter of this.placementRefreshWaiters) waiter(list);
    this.placementRefreshWaiters.clear();
    this.emitPlacementSnapshot();
  }

  private applyItemPlacements(placements: ItemPlacementNode[]): void {
    this.itemPlacements = new Map(placements
      .filter((placement) => this.isOwnProject(placement.projectId))
      .map((placement) => [placement.itemId, mapItemPlacement(placement)]));
    this.confirmations.loaded('item', this.itemPlacements);
    const list = [...this.itemPlacements.values()];
    for (const waiter of this.itemPlacementRefreshWaiters) waiter(list);
    this.itemPlacementRefreshWaiters.clear();
    this.emitPlacementSnapshot();
  }

  private emitSnapshotIfReady(): void {
    if (!this.documentsReady || !this.foldersReady) return;
    this.resyncInFlight = false;
    const snapshot = this.currentSnapshot();
    this.setStatus('connected');
    this.emit({ type: 'snapshot', snapshot });
    for (const waiter of this.snapshotWaiters) waiter.resolve(snapshot);
    this.snapshotWaiters.clear();
    this.replayPendingMutations();
  }

  private isOpen(): boolean {
    return this.socket?.readyState === 1;
  }

  private send(message: TeamClientMessage): void {
    if (this.socket && this.isOpen()) {
      this.socket.send(JSON.stringify(message));
      return;
    }
    const key = mutationKey(message);
    if (key) {
      // One queued message per page and kind; two field patches merge, the later key winning.
      const queued = this.pendingMutations.get(key);
      this.pendingMutations.set(key, queued?.type === 'docIndexSetFields' && message.type === 'docIndexSetFields'
        ? { ...message, fields: { ...queued.fields, ...message.fields } }
        : message);
      return;
    }
    if (message.type === 'documentCommentNotify') {
      // A replay after a long reconnect can surface a stale mention. Prefer a
      // reported loss over delayed delivery that looks newly relevant.
      console.warn(
        `[WebCollabDocumentsDataSource] Dropped ${message.reason} notification while TeamRoom is disconnected`,
      );
    }
  }

  private replayPendingMutations(): void {
    const messages = [...this.pendingMutations.values()];
    this.pendingMutations.clear();
    for (const message of messages) this.send(message);
  }

  private refreshFolders(timeoutMs = 6_000): Promise<SharedFolder[] | null> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (folders: SharedFolder[] | null) => {
        if (settled) return;
        settled = true;
        this.folderRefreshWaiters.delete(finish);
        resolve(folders);
      };
      this.folderRefreshWaiters.add(finish);
      window.setTimeout(() => finish(null), timeoutMs);
      this.send({ type: 'folderIndexSync' });
    });
  }

  private refreshTypePlacements(timeoutMs = 6_000): Promise<TypePlacement[] | null> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (placements: TypePlacement[] | null) => {
        if (settled) return;
        settled = true;
        this.placementRefreshWaiters.delete(finish);
        resolve(placements);
      };
      this.placementRefreshWaiters.add(finish);
      window.setTimeout(() => finish(null), timeoutMs);
      this.send({ type: 'typePlacementIndexSync' });
    });
  }

  private refreshItemPlacements(timeoutMs = 6_000): Promise<ItemPlacement[] | null> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (placements: ItemPlacement[] | null) => {
        if (settled) return;
        settled = true;
        this.itemPlacementRefreshWaiters.delete(finish);
        resolve(placements);
      };
      this.itemPlacementRefreshWaiters.add(finish);
      setTimeout(() => finish(null), timeoutMs);
      this.send({ type: 'itemPlacementIndexSync' });
    });
  }

  private reconnectNow(): void {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    void this.ensureConnected();
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.reconnectTimer) return;
    const delays = this.options.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS_MS;
    const delay = delays[Math.min(this.reconnectAttempt, delays.length - 1)] ?? 30_000;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.ensureConnected();
    }, delay);
  }
}
